/**
 * Container/codec choices the server video renderer can produce.
 *
 * MP4/H.264 plays everywhere and stays the default. WebM/VP9 is for the
 * targets that need it (web embedding without an MP4 licence question,
 * tooling that only ingests WebM) — the client-side MediaRecorder path this
 * replaces produced WebM too, so the option must survive the move to the
 * server renderer.
 */
export const VIDEO_EXPORT_FORMATS = {
  mp4: {
    id: "mp4",
    label: "MP4 (H.264)",
    extension: "mp4",
    mimeType: "video/mp4",
  },
  webm: {
    id: "webm",
    label: "WebM (VP9)",
    extension: "webm",
    mimeType: "video/webm",
  },
} as const;

export type VideoExportFormatId = keyof typeof VIDEO_EXPORT_FORMATS;
export type VideoExportFormat = (typeof VIDEO_EXPORT_FORMATS)[VideoExportFormatId];

export const VIDEO_EXPORT_FORMAT_LIST = Object.values(VIDEO_EXPORT_FORMATS);

/** What a request without an explicit format gets — see parseVideoRenderInput. */
export const DEFAULT_VIDEO_EXPORT_FORMAT: VideoExportFormat = VIDEO_EXPORT_FORMATS.mp4;

export function getVideoExportFormat(value: unknown): VideoExportFormat {
  if (typeof value !== "string") {
    throw new Error("Video export format must be a string");
  }
  if (!Object.prototype.hasOwnProperty.call(VIDEO_EXPORT_FORMATS, value)) {
    throw new Error(`Unsupported video export format ${JSON.stringify(value)}`);
  }
  return VIDEO_EXPORT_FORMATS[value as VideoExportFormatId];
}
